import { useMemo, useRef, useState } from 'react'

import { Input, message } from 'antd'
import type { TextAreaRef } from 'antd/es/input/TextArea'
import dayjs from 'dayjs'
import DOMPurify from 'dompurify'

import { useMessageAtom } from '@/pages/MesperyIM/store/message'
import { useUserAtom } from '@/store/user'
import { handleClickSendMessage, handleSendSuccess } from '@/utils/trackSensors'
import SendActiveSrc from '@img/message/send-active.png'
import SendSrc from '@img/message/send.png'

import { ToolBar } from './ToolBar'
import { ChatPanelInputContext } from '../context'
import useSWRSessionList from '../hooks/useSWRSessionList'
import { useWs } from '../providers/WebsocketProvider'
import { EMessageType } from '../types/message'
import { formatSpaces, sortSessionsById } from '../utils'

const { TextArea } = Input

export const CUSTOMER_SERVICE_ACCOUNT_MAP = new Map([
  [import.meta.env.VITE_WS_AUTHOR_AGENT, 'AUTHOR_AGENT'],
  [import.meta.env.VITE_WS_CUSTOMER_SERVICE, 'CUSTOMER_SERVICE']
])

export const ChatPanelInput = () => {
  const [btnDisabled, setBtnDisabled] = useState(true)
  const [value, setValue] = useState<string>()
  const {
    currentConversation,
    messageData,
    setMessageData,
    conversationList,
    setConversationList,
    loadMoreHistory
  } = useMessageAtom()
  const textareaRef = useRef<TextAreaRef>(null)
  const { sendRequest } = useWs()
  const { user } = useUserAtom()
  const { mutate, data: swrSessionList } = useSWRSessionList()

  const disabledAction = useMemo(() => {
    const disabledUser = ['SYSTEM_MESSAGE'].includes(
      currentConversation?.session_id || ''
    )

    return disabledUser || loadMoreHistory
  }, [currentConversation, loadMoreHistory])

  const inputChange = (value?: string) => {
    setValue(value)
    setBtnDisabled(value === '')
  }

  const handleSendMessage = async () => {
    if (!value) {
      return message.warning('Send content cannot be empty')
    }

    if (value.replaceAll(' ', '') === '') {
      return message.warning('Send content cannot be empty')
    }
    if (value) {
      const messageContent = DOMPurify.sanitize(formatSpaces(value), {
        ALLOWED_TAGS: ['br']
      })
      const sendData = {
        receiver_type: currentConversation?.session_type,
        receiver_id: currentConversation?.session_id,
        message_type: EMessageType.TxtMsg,
        message_content: messageContent
      }
      handleClickSendMessage(
        currentConversation?.session_id || '',
        currentConversation?.session_type || 0,
        EMessageType.TxtMsg,
        CUSTOMER_SERVICE_ACCOUNT_MAP.get(user.id) || user.id
      )
      const resp = await sendRequest('SEND_MESSAGE', sendData)
      const sendTime = dayjs().unix()

      if (messageData?.list && resp) {
        // 全新的会话，需要重新获取会话列表数据
        if (
          !conversationList?.some(
            (item) => item.session_id === currentConversation?.session_id
          )
        ) {
          mutate(swrSessionList, {
            revalidate: (pageData, key) =>
              key === '/imApi/session/list?page=1&size=20'
          })
        }
        handleSendSuccess(
          currentConversation?.session_id || '',
          currentConversation?.session_type || 0,
          EMessageType.TxtMsg,
          CUSTOMER_SERVICE_ACCOUNT_MAP.get(user.id) || user.id
        )
        messageData.list = [
          ...messageData.list,
          {
            id: '',
            created_at: String(sendTime),

            message_content: messageContent,

            message_type: EMessageType.TxtMsg,

            sender_user_id: CUSTOMER_SERVICE_ACCOUNT_MAP.get(user.id) || user.id
          }
        ]
        setMessageData({ ...messageData })
        setValue('')

        const sessionList = conversationList?.map((item) => {
          if (item.session_id === currentConversation?.session_id) {
            item.last_message_content = messageContent
            item.last_message_created_at = sendTime
            item.last_read_at = sendTime
            item.last_message_type = EMessageType.TxtMsg
            item.last_message_sender_id =
              CUSTOMER_SERVICE_ACCOUNT_MAP.get(user.id) || user.id
          }
          return item
        })

        let sortList = sessionList
        if (sessionList) {
          sortList = sortSessionsById(
            sessionList,
            currentConversation?.session_id
          )
        }
        setConversationList(sortList)

        setTimeout(() => {
          // 获取容器元素
          const container = document.getElementById('scrollableDivIM')
          // 将容器滚动到底部
          if (container) {
            container.scrollTop = container.scrollHeight
          }
        }, 100)
      }
    }
  }

  const ChatPanelInputContextVal = useMemo(() => {
    return {
      textareaRef,
      inputChange
    }
  }, [textareaRef])

  return (
    <ChatPanelInputContext.Provider value={ChatPanelInputContextVal}>
      <div className="ChatPanelInput">
        {/* 操作栏 */}
        <ToolBar disabled={disabledAction} />

        {/* 输入框 */}
        <div className="sendBox">
          <TextArea
            ref={textareaRef}
            value={value}
            onChange={(e) => {
              inputChange(e.target.value)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                handleSendMessage()
              }
            }}
            placeholder="Text what you want..."
            autoSize={{ minRows: 5, maxRows: 5 }}
            className="send-textarea"
            disabled={disabledAction}
          />
        </div>

        {/* 发送 */}
        <div className="tr">
          <button
            className={`g-btn ${disabledAction ? '' : 'g-btn-primary'}`}
            disabled={disabledAction || btnDisabled}
            onClick={handleSendMessage}
          >
            <img src={disabledAction ? SendSrc : SendActiveSrc} width={17} />
          </button>
        </div>
      </div>
    </ChatPanelInputContext.Provider>
  )
}
